const DEFAULT_FALLBACK_MAX_VH = 200;
const DEFAULT_FALLBACK_MULTIPLIER = 4;
const DEFAULT_ADDITIONAL_LINES = 7;
const DEFAULT_LINE_HEIGHT = 25;
export default class KeyboardAwareComposerHeight {
  constructor(target, options = {}) {
    this.target = target;
    this.options = {
      fallbackMaxVh: DEFAULT_FALLBACK_MAX_VH,
      fallbackMultiplier: DEFAULT_FALLBACK_MULTIPLIER,
      additionalLines: DEFAULT_ADDITIONAL_LINES,
      ...options
    };
    this.listeners = [];
    this.handleEvent = this.handleEvent.bind(this);
    this.update = this.update.bind(this);
    this.raf = null;
    this.traceEntries = [];
    this.initialize();
  }
  initialize() {
    if (!this.target) return;
    this.target.classList.add('TextEditor--keyboardAware');
    if (!this.hasWindow()) return;
    this.listen(window, 'resize');
    this.listen(window, 'orientationchange');
    this.listen(document, 'visibilitychange');
    if (window.visualViewport) {
      this.listen(window.visualViewport, 'resize');
      this.listen(window.visualViewport, 'scroll');
    }
    this.scheduleUpdate();
  }
  hasWindow() {
    return typeof window !== 'undefined';
  }
  listen(target, event) {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener(event, this.handleEvent, {
      passive: true
    });
    this.listeners.push({
      target,
      event
    });
  }
  handleEvent() {
    this.scheduleUpdate();
  }
  scheduleUpdate() {
    if (!this.hasWindow()) return;
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.raf = window.requestAnimationFrame(this.update);
  }
  update() {
    this.raf = null;
    if (!this.target || !this.hasWindow()) return;
    const viewportHeight = Math.max(1, this.getViewportHeight());
    const rect = this.target.getBoundingClientRect();
    const baseHeight = rect.height > 0 ? rect.height : viewportHeight * 0.35;
    const fallbackHeight = Math.min(viewportHeight, baseHeight * this.options.fallbackMultiplier);
    const fallbackLimit = Math.min(viewportHeight, viewportHeight * this.options.fallbackMaxVh / 100);
    const detectionAvailable = !!window.visualViewport;
    const availableSpace = Math.max(0, viewportHeight - rect.top);
    const extraLinesHeight = this.getLineHeight() * this.options.additionalLines;
    const fallbackHeightWithExtra = fallbackHeight + extraLinesHeight;
    let height;
    if (detectionAvailable) {
      height = availableSpace + extraLinesHeight;
    } else {
      height = fallbackHeightWithExtra;
    }
    height = Math.min(height, fallbackLimit);
    height = Math.max(height, fallbackHeightWithExtra);
    this.trace('update', {
      viewportHeight,
      rectTop: rect.top,
      availableSpace,
      detectionAvailable,
      extraLinesHeight,
      fallbackHeight,
      fallbackLimit,
      finalHeight: height
    });
    this.trace('update', {
      viewportHeight,
      rectTop: rect.top,
      availableSpace,
      detectionAvailable,
      extraLinesHeight,
      fallbackHeight,
      fallbackLimit,
      finalHeight: height
    });
    this.target.style.setProperty('--aurrera-composer-max-height', `${height}px`);
  }
  getViewportHeight() {
    if (window.visualViewport && typeof window.visualViewport.height === 'number') {
      return window.visualViewport.height;
    }
    return window.innerHeight;
  }
  getLineHeight() {
    if (!this.target) return DEFAULT_LINE_HEIGHT;
    const editorElement = this.target.querySelector('.ProseMirror');
    const computedStyle = window.getComputedStyle(editorElement || this.target);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    if (Number.isFinite(lineHeight) && lineHeight > 0) {
      return lineHeight;
    }
    const fontSize = parseFloat(computedStyle.fontSize);
    if (Number.isFinite(fontSize) && fontSize > 0) {
      return fontSize * 1.4;
    }
    return DEFAULT_LINE_HEIGHT;
  }
  recalculate() {
    this.scheduleUpdate();
  }
  destroy() {
    this.listeners.forEach(({
      target,
      event
    }) => {
      if (target && typeof target.removeEventListener === 'function') {
        target.removeEventListener(event, this.handleEvent);
      }
    });
    this.listeners = [];
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    if (this.target) {
      this.target.classList.remove('TextEditor--keyboardAware');
      this.target.style.removeProperty('--aurrera-composer-max-height');
    }
  }
  trace(label, payload) {
    const entry = {
      label,
      payload,
      timestamp: Date.now()
    };
    this.traceEntries.push(entry);
    if (typeof window !== 'undefined') {
      window.__aurreraComposerTraces = window.__aurreraComposerTraces || [];
      window.__aurreraComposerTraces.push(entry);
    }
  }
}
flarum.reg.add('bizkaitarra-aurrera-editor', 'common/utils/KeyboardAwareComposerHeight', KeyboardAwareComposerHeight);